import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  addCardNumberInput,
  addIssuerReceiptEmailInput,
  createServiceRequestPaymentInputPresModuleForm,
} from '@app/service-request-payment-input/presenter/service-request-payment-input-pres.form';
import {
  LISTPAYMENTMETHOD,
  PaymentMethodsType,
  REMOVE_VALIDATIONS_FORMS,
} from '@common/helpers/constants/service-request-payment-input/service-request-payment-input.constants';
import { ServiceRequestPaymentInputService } from '@common/services/service-request-payment-input/service-request-payment-input-service';
import { SupportComponent } from '@lib/components/support-class';
import { DeviceType, LoginStatusType } from '@lib/interfaces';
import { CommonLibService } from '@lib/services';
import { Subscription, combineLatest } from 'rxjs';

@Component({
  selector: 'asw-service-request-payment-input-payment-methods',
  templateUrl: './service-request-payment-input-payment-methods.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServiceRequestPaymentInputPaymentMethodsComponent extends SupportComponent {
  listPaymentMethod = LISTPAYMENTMETHOD;

  _subscriptions: Subscription = new Subscription();

  public isDisplayPaymentMethodSelectionSP = false;

  public isSp: boolean = false;

  public posCountryCode = this._common.aswContextStoreService.aswContextData.posCountryCode;

  public isSaveAsUsualChecked: boolean = false;

  public paymentMethod!: string;

  public paymentMethodDisplayList: { [key: string]: { value: boolean } } = {};

  public isPaddingItem = false;

  get paymentInputFG(): FormGroup {
    return this._serviceRequestPaymentInputService.paymentInputFG;
  }

  constructor(
    private readonly _common: CommonLibService,
    private readonly _changeDetector: ChangeDetectorRef,
    private readonly _serviceRequestPaymentInputService: ServiceRequestPaymentInputService
  ) {
    super(_common);
  }

  init(): void {
    // [3.2] 選択済支払方法
    if (this._common.aswContextStoreService.aswContextData.deviceType === DeviceType.SMART_PHONE) {
      this.isSp = true;
      this.isDisplayPaymentMethodSelectionSP = true;
    }

    this._subscriptions.add(
      combineLatest([
        this._serviceRequestPaymentInputService.paymentMethod$,
        this._serviceRequestPaymentInputService.paymentMethodDisplayList$,
      ]).subscribe(([paymentMethod, paymentMethodDisplayList]) => {
        this.paymentMethod = paymentMethod;

        // [3.1] -> [3.5] 支払方法選択
        this.listPaymentMethod = this.listPaymentMethod.map((paymentItem) => {
          return {
            ...paymentItem,
            isDisplay: paymentMethodDisplayList[paymentItem.paymentMethod]?.value,
          };
        });
        this.listPaymentMethod.sort((a, b) => (b.isDisplay ? 1 : 0) - (a.isDisplay ? 1 : 0));
        let count = this.listPaymentMethod.filter((item) => item.isDisplay).length;
        this.isPaddingItem = count % 2 !== 0;
        this._changeDetector.detectChanges();
      })
    );
  }

  public refresh() {
    this.paymentInputFG.get('saveAsUsualCheckboxControl')?.setValue(this.isSaveAsUsualChecked);
    this._changeDetector.markForCheck();
  }

  // (3) 支払方法選択ラジオボタン切り替え処理
  public onChangePaymentMethod(value: string) {
    const params = this._serviceRequestPaymentInputService.dynamicSubject.getValue();
    this._serviceRequestPaymentInputService.dynamicSubject.next({
      ...params,
      pageContext: {
        ...params.pageContext,
        currentPaymentMethod: value,
      },
    });
    // (3.2)
    if (this.isSp) {
      this.isDisplayPaymentMethodSelectionSP = true;
    }
    // (3.1)
    if (value !== PaymentMethodsType.CREDIT_CARD) {
      this._serviceRequestPaymentInputService.removeControlsByMethod(value);
    } else {
      const cardNumber = addCardNumberInput();
      const issuerReceiptEmail = addIssuerReceiptEmailInput();
      const additionalForm = createServiceRequestPaymentInputPresModuleForm();
      additionalForm.addControl('cardNumber', cardNumber);
      additionalForm.addControl('issuerReceiptEmail', issuerReceiptEmail);
      // add control for CREDIT_CARD
      REMOVE_VALIDATIONS_FORMS.forEach((key) => {
        if (!this.paymentInputFG.get(key)) {
          this.paymentInputFG.addControl(key, additionalForm.get(key));
        }
      });
    }
    this.paymentInputFG.markAsPristine();
    this.paymentInputFG.markAsUntouched();
    this._changeDetector.markForCheck();
    this._serviceRequestPaymentInputService.paymentMethod$.next(value);
  }

  // (4) 支払方法変更ボタン押下処理※SP版のみ
  onPaymentMethodChangeSP() {
    this.isDisplayPaymentMethodSelectionSP = false;
    this._changeDetector.markForCheck();
  }

  // [3.9] 予約基本情報のいつもの支払方法登録チェック
  get isDisplayCreditCardSelected(): boolean {
    return (
      this._common.aswContextStoreService.aswContextData.loginStatus === LoginStatusType.REAL_LOGIN &&
      this.paymentMethod === PaymentMethodsType.CREDIT_CARD &&
      this._common.aswContextStoreService.aswContextData.posCountryCode !== 'IN'
    );
  }

  isCurrentMethod(type: string): boolean {
    return type === this.paymentMethod;
  }

  isCreditCard(type: string): boolean {
    return type === PaymentMethodsType.CREDIT_CARD;
  }

  // [3.4.1.1] 支払規約：クレジットカード
  get cardCreditLabel(): string {
    return this.posCountryCode === 'JP' ? 'label.creditCard' : 'heading.creditCardOrDebitCardForTitle';
  }
  // [3.7] 選択済支払方法ラベル
  getPaymentMethod(code: string = ''): string {
    switch (code) {
      case PaymentMethodsType.CREDIT_CARD:
        return 'cc';
      case PaymentMethodsType.PAYPAL:
        return 'paypal';
      case PaymentMethodsType.UNION_PAY:
        return 'unionpay';
      case PaymentMethodsType.ALIPAY:
        return 'alipay';
      default:
        return '';
    }
  }

  getVisiblePaymentMethods() {
    return this.listPaymentMethod.filter((method) => method.isDisplay);
  }

  destroy(): void {
    if (this._subscriptions) {
      this._subscriptions.unsubscribe();
    }
  }

  reload(): void {
    /* override abstract reload function of base class */
  }
}
